import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Users, Music, Download } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

// Custom hook: animated count-up that respects a target value
function useAnimatedCounter(target, duration = 1800) {
  const [value, setValue] = useState(0);
  const rafRef = useRef(null);
  const startRef = useRef(null);
  const fromRef = useRef(0);

  useEffect(() => {
    if (target === null || target === undefined) return;
    fromRef.current = value;
    startRef.current = null;
    const step = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const p = Math.min(1, (ts - startRef.current) / duration);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - p, 3);
      const next = Math.floor(fromRef.current + (target - fromRef.current) * eased);
      setValue(next);
      if (p < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  return value;
}

function formatNumber(n) {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('es-ES').format(n);
}

const Stat = ({ icon: Icon, value, label, accent }) => (
  <div
    className="flex flex-col sm:flex-row items-center sm:gap-2 gap-1 px-1.5 sm:px-3 md:px-5 py-2 shrink-0"
    data-testid={`community-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className={`w-7 h-7 sm:w-9 sm:h-9 md:w-10 md:h-10 rounded-lg sm:rounded-xl flex items-center justify-center shrink-0 ${accent}`}>
      <Icon className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 text-[#E8720C]" />
    </div>
    <div className="flex flex-col leading-tight text-center sm:text-left">
      <span
        className="text-sm sm:text-lg md:text-xl lg:text-2xl font-bold text-white tabular-nums tracking-tight"
        data-testid={`community-stat-value-${label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        {formatNumber(value)}
      </span>
      <span className="text-[8px] sm:text-[9px] md:text-[10px] uppercase tracking-[0.1em] sm:tracking-[0.18em] text-white/55 font-semibold whitespace-nowrap">
        {label}
      </span>
    </div>
  </div>
);

export default function CommunityStats() {
  const { language } = useLanguage();
  const isEs = language === 'es';
  const L = isEs
    ? { live: 'En Vivo · Comunidad OG', members: 'Miembros', tracks: 'Tracks', downloads: 'Descargas', trust: 'Producers verifican cada track' }
    : { live: 'Live · OG Community',    members: 'Members',  tracks: 'Tracks', downloads: 'Downloads',  trust: 'Producers verify every track' };
  const [targets, setTargets] = useState({ members: null, tracks: null, downloads: null });
  const tickerIntervalRef = useRef(null);

  // Fetch real counts from backend
  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${API}/api/stats/community`, { timeout: 10000 });
      setTargets({
        members: data.members || 0,
        tracks: data.tracks || 0,
        downloads: data.downloads || 0,
      });
    } catch (e) {
      // graceful fallback
      setTargets((prev) => prev.members === null ? { members: 5000, tracks: 0, downloads: 14000 } : prev);
    }
  };

  useEffect(() => {
    fetchStats();

    // Refresh real counts every 5 minutes to catch new signups/purchases
    const refreshInterval = setInterval(fetchStats, 5 * 60 * 1000);

    // Live ticker: small random increments every 25-55s to feel active
    // (reconciles to real count on next fetchStats() refresh)
    const scheduleTicker = () => {
      const delay = 25000 + Math.random() * 30000; // 25-55s
      tickerIntervalRef.current = setTimeout(() => {
        setTargets((prev) => {
          if (prev.members === null) return prev;
          const bump = 1 + Math.floor(Math.random() * 3); // +1 to +3
          // 60% of ticks bump members, 25% downloads, 15% both
          const r = Math.random();
          if (r < 0.6) return { ...prev, members: prev.members + bump };
          if (r < 0.85) return { ...prev, downloads: prev.downloads + bump };
          return { ...prev, members: prev.members + bump, downloads: prev.downloads + bump };
        });
        scheduleTicker();
      }, delay);
    };
    scheduleTicker();

    return () => {
      clearInterval(refreshInterval);
      if (tickerIntervalRef.current) clearTimeout(tickerIntervalRef.current);
    };
  }, []);

  const membersAnim = useAnimatedCounter(targets.members);
  const tracksAnim = useAnimatedCounter(targets.tracks);
  const downloadsAnim = useAnimatedCounter(targets.downloads);

  return (
    <div
      className="relative z-30 w-full md:border-b md:border-white/5 md:bg-gradient-to-r md:from-[#0a0a0c] md:via-[#0c0c10] md:to-[#0a0a0c] rounded-2xl md:rounded-none bg-white/[0.03] border border-white/10 md:border-x-0 md:border-t-0 backdrop-blur-sm overflow-hidden"
      data-testid="community-stats-bar"
    >
      <div className="px-3 md:container-custom py-3 md:py-5">
        <div className="flex flex-col md:flex-row items-center justify-center md:justify-between gap-2 md:gap-0">
          {/* Live indicator */}
          <div className="flex items-center gap-2 text-[9px] md:text-xs uppercase tracking-[0.2em] md:tracking-[0.25em] text-white/50 md:text-white/45 font-semibold order-first md:order-none">
            <span className="relative flex w-1.5 h-1.5 md:w-2 md:h-2">
              <span className="absolute inline-flex w-full h-full rounded-full bg-[#E8720C] opacity-70 animate-ping" />
              <span className="relative inline-flex w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-[#E8720C]" />
            </span>
            <span>{L.live}</span>
          </div>

          {/* Stats — stack vertically on mobile (<640px), row on tablet+ */}
          <div className="grid grid-cols-3 sm:flex sm:flex-nowrap items-center justify-center sm:divide-x sm:divide-white/5 w-full md:w-auto">
            <Stat icon={Users} value={membersAnim} label={L.members} accent="bg-[#E8720C]/10 border border-[#E8720C]/20" />
            <Stat icon={Music} value={tracksAnim} label={L.tracks} accent="bg-[#E8720C]/10 border border-[#E8720C]/20" />
            <Stat icon={Download} value={downloadsAnim} label={L.downloads} accent="bg-[#E8720C]/10 border border-[#E8720C]/20" />
          </div>

          {/* Trust badge */}
          <div className="hidden lg:flex items-center gap-2 text-[10px] xl:text-xs uppercase tracking-[0.25em] text-white/45 font-semibold">
            <span className="text-[#E8720C]">★</span>
            <span>{L.trust}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
